/**
 * Edit Mode Hook
 *
 * State machine for section edit mode (view ↔ edit transitions).
 * Handles draft persistence, unsaved changes detection, optimistic UI updates.
 *
 * Epic: MVP 1.20 - Dynamic Experience Page Layout
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Edit mode state machine
 * States: 'view' | 'edit' | 'saving' | 'error'
 *
 * @param {Object} initialData - Initial section data
 * @param {Function} onSave - Save callback (contentData, isDraft) => Promise<data>
 * @returns {Object} Edit mode state and actions
 */
export function useEditMode(initialData, onSave) {
  const [mode, setMode] = useState('view');
  const [data, setData] = useState(initialData?.contentData || {});
  const [originalData, setOriginalData] = useState(initialData?.contentData || {});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Track if component is mounted
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Update data when initialData changes
  useEffect(() => {
    if (initialData?.contentData) {
      setData(initialData.contentData);
      setOriginalData(initialData.contentData);
      setHasUnsavedChanges(false);
      // Reset to view mode if we're in saving mode (after successful save)
      setMode(prevMode => prevMode === 'saving' ? 'view' : prevMode);
    }
  }, [initialData]);

  // Detect unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(data) !== JSON.stringify(originalData);
    setHasUnsavedChanges(hasChanges);
  }, [data, originalData]);

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Enter edit mode
   */
  const enterEditMode = useCallback(() => {
    setMode('edit');
    setError(null);
  }, []);

  /**
   * Cancel edit mode (discard changes)
   */
  const cancelEdit = useCallback(() => {
    if (hasUnsavedChanges) {
      // Show confirmation modal
      setShowCancelConfirm(true);
      return;
    }

    setData(originalData);
    setMode('view');
    setError(null);
    setHasUnsavedChanges(false);
  }, [hasUnsavedChanges, originalData]);

  /**
   * Confirm cancel (called after user confirms)
   */
  const confirmCancelEdit = useCallback(() => {
    setData(originalData);
    setMode('view');
    setError(null);
    setHasUnsavedChanges(false);
    setShowCancelConfirm(false);
  }, [originalData]);

  /**
   * Decline cancel (stay in edit mode)
   */
  const declineCancelEdit = useCallback(() => {
    setShowCancelConfirm(false);
  }, []);

  /**
   * Update field value
   */
  const updateField = useCallback((fieldName, value) => {
    setData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  }, []);

  /**
   * Update multiple fields at once
   */
  const updateFields = useCallback((updates) => {
    setData(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  /**
   * Save changes (draft or published)
   */
  const saveChanges = useCallback(async (isDraft = true) => {
    try {
      setMode('saving');
      setError(null);

      // Call save callback
      const savedData = await onSave(data, isDraft);

      if (!isMounted.current) {
        return;
      }

      // Update original data to mark as saved
      // savedData might have content_data (snake_case) or contentData (camelCase)
      const newData = savedData.content_data || savedData.contentData || savedData || data;

      setOriginalData(newData);
      setData(newData);
      setHasUnsavedChanges(false);

      // Force transition back to view mode
      setMode('view');

      return savedData;
    } catch (err) {
      if (!isMounted.current) {return;}

      console.error('[useEditMode] Save error:', err);
      setError(err.message);
      setMode('edit'); // Stay in edit mode on error
      throw err;
    }
  }, [data, onSave]);

  /**
   * Save draft
   */
  const saveDraft = useCallback(() => {
    return saveChanges(true);
  }, [saveChanges]);

  /**
   * Save and publish
   */
  const saveAndPublish = useCallback(() => {
    return saveChanges(false);
  }, [saveChanges]);

  // ============================================================================
  // Warn on page leave with unsaved changes
  // ============================================================================

  useEffect(() => {
    if (!hasUnsavedChanges) {return;}

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  return {
    // State
    mode,
    data,
    hasUnsavedChanges,
    error,
    isEditing: mode === 'edit',
    isSaving: mode === 'saving',
    canSave: hasUnsavedChanges && mode === 'edit',
    showCancelConfirm,

    // Actions
    enterEditMode,
    cancelEdit,
    confirmCancelEdit,
    declineCancelEdit,
    updateField,
    updateFields,
    saveDraft,
    saveAndPublish
  };
}

export default useEditMode;
